import { List, useTable } from '@refinedev/antd';
import { useInvalidate } from '@refinedev/core';
import { useNavigate, useSearchParams } from 'react-router';
import { Link } from 'react-router';
import type { Conference } from '@viq-admin/types';
import { archiveConference } from '../../api/conferences';
import { useCompanyScope } from '../../components/CompanyScopePicker';
import {
  conferenceCopy,
  DataTable,
  RowActionsMenu,
  useUrlTableState,
  type DataColumn,
  type TableState,
} from '@viq-admin/ui';

/**
 * Seeds the default sort when the URL carries none. Module-level so its
 * identity is stable across renders — an inline literal here would bust
 * DataTable's `effective` memo (and, through it, `prepared`/`finalColumns`)
 * on every render for no reason. See DataTable's `defaultState` JSDoc.
 */
const DEFAULT_STATE: Partial<TableState> = {
  sort: { field: 'name', order: 'ascend' },
};

/**
 * All-tenant list of active conferences.
 *
 * Read access gates the page (via the route's RequireCan); `manage:Conference`
 * gates the per-row Archive action. Opening a conference is the ROW CLICK —
 * there is deliberately no Edit or View button (see the DataTable spec).
 */
export function ConferenceList() {
  const { tableProps } = useTable<Conference>({
    resource: 'conferences',
    // DataTable owns paging; Refine must not slice the payload.
    pagination: { mode: 'off' },
  });
  const invalidate = useInvalidate();
  const navigate = useNavigate();
  // Scope-aware: a tenant switch swaps the row set — reset the table's sticky
  // rows so it loads fresh rather than holding the previous tenant's list.
  const { companyId: scope } = useCompanyScope();
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useUrlTableState(searchParams, setSearchParams);

  const columns: DataColumn<Conference>[] = [
    { dataIndex: 'name', key: 'name', title: 'Name', kind: 'text', width: 260 },
    { dataIndex: 'location', key: 'location', title: 'Location', kind: 'text', width: 200 },
    { dataIndex: 'startDate', key: 'startDate', title: 'Starts', kind: 'date' },
    { dataIndex: 'endDate', key: 'endDate', title: 'Ends', kind: 'date' },
    {
      key: 'actions',
      title: '',
      kind: 'actions',
      render: (_: unknown, record: Conference) => (
        <RowActionsMenu
          actions={[
            {
              key: 'archive',
              label: 'Archive',
              danger: true,
              cap: 'manage:Conference',
              onRun: async () => {
                await archiveConference(record.id);
                invalidate({ resource: 'conferences', invalidates: ['list'] });
              },
              confirm: {
                title: `Archive ${record.name}?`,
                content: conferenceCopy.archiveBodyWithRestoreHint,
                okText: 'Archive',
                danger: true,
                successMessage: `Archived ${record.name}.`,
              },
            },
          ]}
        />
      ),
    },
  ];

  return (
    <List
      headerButtons={({ defaultButtons }) => (
        <>
          {defaultButtons}
          <Link to="/conferences/archived">View archived →</Link>
        </>
      )}
    >
      <DataTable<Conference>
        tableProps={tableProps}
        columns={columns}
        rowKey="id"
        storageKey="conferencesList"
        resetKey={scope}
        state={state}
        onStateChange={setState}
        defaultState={DEFAULT_STATE}
        searchFields={['name', 'location']}
        searchPlaceholder="Search conferences"
        onRowClick={(record) => navigate(`/conferences/${record.id}`)}
        empty={{
          title: 'No conferences yet',
          description: 'Create the first conference to get started.',
        }}
      />
    </List>
  );
}
